// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { useEffect, useState } from "react";

import { debounce } from "lodash-es";

import { useSearchParams } from "hooks/utils";

import { createSearchParams } from "../_utils";
import { AvailableFilters, Filter } from "../_utils/types";
import { SUGGESTIONS_REQUEST_DEBOUNCE } from "../constants";
import { useFilters } from "./useFilters";

const scheduleSuggestionsRefresh = debounce(
  async (searchBarValue: string, backendSuggestions) => {
    backendSuggestions.refreshSuggestions(searchBarValue);
  },
  SUGGESTIONS_REQUEST_DEBOUNCE
);

type SearchParams = {
  contains?: string;
  not_contains?: string;
  search?: string;
};

type UseSearchBar = (props: {
  availableFilters: AvailableFilters;
  backendSuggestions: any;
  onSearch?: (input: SearchParams) => void;
  onChange?: (searchbarValue: string) => void;
  validator?: (input: SearchParams) => SearchParams;
}) => {
  searchValue: string;
  setSearchValue: (newSearchValue: string) => void;
  validationError: string;
  onSearchBarChangeHandler: (newSearchValue: string) => void;
  onSearchHandler: (searchValue: string, canStartTextSearch: boolean) => void;
  includeFilters: Filter[];
  excludeFilters: Filter[];
  addFilter: (searchValue: string) => void;
  removeFilter: (filterIdx: number) => void;
};

export const useSearchBar: UseSearchBar = ({
  onChange,
  onSearch,
  backendSuggestions,
  availableFilters,
  validator = (searchParams) => searchParams,
}) => {
  const searchParams = useSearchParams();
  const [searchValue, setSearchValue] = useState(
    (searchParams?.search as string) || ""
  );
  const [validationError, setValidationError] = useState("");

  const { includeFilters, excludeFilters, addFilter, removeFilter } =
    useFilters({
      availableFilters,
      suggestions: backendSuggestions.suggestions,
    });

  const startNewSearch = (value: string) => {
    backendSuggestions.abortFetchingSuggestions();
    setValidationError("");
    try {
      const newSearchParams = validator(
        createSearchParams(searchParams, {
          value: value,
          filters: includeFilters,
          exclusionFilters: excludeFilters,
        })
      );
      onSearch?.(newSearchParams);
    } catch (err: any) {
      setValidationError(err.errors);
    }
  };

  const onSearchBarChangeHandler = (newSearchValue: string) => {
    setSearchValue(newSearchValue);
    backendSuggestions.abortFetchingSuggestions(newSearchValue);

    if (onChange) {
      onChange(newSearchValue);
    }
  };

  const onSearchHandler = (
    searchValue: string,
    canStartTextSearch: boolean
  ) => {
    const trimmedValue = searchValue.trim();
    setSearchValue(trimmedValue);

    const isFilterAdded = addFilter(trimmedValue);
    const canPerformTextSearch =
      canStartTextSearch && !backendSuggestions.isLoadingSuggestions;

    if (isFilterAdded) {
      setSearchValue("");
      return;
    }

    if (canPerformTextSearch) {
      startNewSearch(trimmedValue);
    }
  };

  useEffect(() => {
    startNewSearch(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeFilters.length, excludeFilters.length]);

  useEffect(() => {
    scheduleSuggestionsRefresh(searchValue, backendSuggestions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return {
    searchValue,
    setSearchValue,
    validationError,
    onSearchBarChangeHandler,
    onSearchHandler,
    addFilter,
    removeFilter,
    includeFilters,
    excludeFilters,
  };
};
